import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { FluentProvider, webLightTheme, Spinner } from "@fluentui/react-components";
import { setIapTokenService } from './utils/iapService';

/* global document, Office, window, module, require, location */

const rootElement = document.getElementById("container");
const root = createRoot(rootElement);

// Render a simple loading spinner initially.
root.render(<Spinner label="Initializing..." />);

/**
 * =========================================================================
 * The Core Application Logic and Authentication Controller
 * =========================================================================
 */
const AuthController = () => {
  const [iapToken, setIapToken] = React.useState(null);
  const [authError, setAuthError] = React.useState(null);
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);

  // This check is reliable because it's only called after initialization.
  const isOfficeEnvironment = () => typeof Office !== "undefined" && Office.context && Office.context.ui;

  const processAuthToken = (event) => {
    const messageString = event.message || event.data;

    if (window.loginDialog) {
      try {
        window.loginDialog.close();
      } catch (e) {
        console.warn("Dialog was already closed.");
      }
    }

    try {
      const message = JSON.parse(messageString);
      if (message.status === "success" && message.session_id) {
        console.log("Authentication successful! Storing IAP token.",message.session_id);
         setIapTokenService(message.session_id);   
        setIapToken(message.session_id);
      } else {
        throw new Error(message.error || "Authentication failed.");
      }
    } catch (e) {
      console.error("Failed to process auth token:", e);
      setAuthError(e.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

//   const startLoginFlow = () => {
//     console.log("Login button clicked. Starting dialog flow...");
//     setIsAuthenticating(true);
//     setAuthError(null);

//     const authUrl = location.origin + '/auth-start.html';

//     if (isOfficeEnvironment()) {
//       // --- OFFICE ADD-IN LOGIC ---
//       console.log("Running in Office. Using displayDialogAsync().");
//       Office.context.ui.displayDialogAsync(authUrl, { height: 60, width: 40 }, (asyncResult) => {
//         if (asyncResult.status === Office.AsyncResultStatus.Failed) {
//           console.error("Dialog failed to open: " + asyncResult.error.message);
//           setAuthError("Could not open authentication window.");
//           setIsAuthenticating(false);
//           return;
//         }
//         window.loginDialog = asyncResult.value;
//         window.loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processAuthToken);
//       });
//     } else {
//       // --- BROWSER FALLBACK LOGIC ---
//       console.log("Running in browser fallback. Using window.open().");
//       const dialogWidth = 800;
//       const dialogHeight = 600;
//       window.loginDialog = window.open(authUrl, 'auth_dialog', `width=${dialogWidth},height=${dialogHeight}`);
//       window.addEventListener("message", processAuthToken, { once: true });
//     }
//   };
// const startLoginFlow = async () => {
//   setIsAuthenticating(true);
//   setAuthError(null);

//   try {
//     // 1. Ask Express to generate a unique login session ID
//     const initResponse = await fetch('/api/auth/initiate', { method: 'POST' });
//     const { loginId } = await initResponse.json();

//     // 2. Open the login page in the user's desktop browser (NOT a dialog)
//     const loginUrl = `${window.location.origin}/auth-start.html?loginId=${loginId}`;
//     Office.context.ui.openBrowserWindow(loginUrl);

//     // 3. Poll the server every 3 seconds to check if the user completed login
//     const pollInterval = setInterval(async () => {
//       const pollResponse = await fetch(`/api/auth/poll?loginId=${loginId}`);
      
//       if (pollResponse.ok) {
//         const data = await pollResponse.json();
//         if (data.token) {
//           clearInterval(pollInterval);
//           console.log("Authentication successful!");
//           setIapToken(data.token); // Store the token in React state
//           setIsAuthenticating(false);
//         }
//       } else if (pollResponse.status === 400) {
//         // Bad request / Session expired
//         clearInterval(pollInterval);
//         setAuthError("Login session expired. Please try again.");
//         setIsAuthenticating(false);
//       }
//     }, 3000);

//   } catch (err) {
//     console.error(err);
//     setAuthError("Failed to initiate login.");
//     setIsAuthenticating(false);
//   }
// };
 const startLoginFlow = async () => {
  console.log("Login button clicked. Starting dialog flow...");
  setIsAuthenticating(true);
  setAuthError(null);

  if (isOfficeEnvironment()) {
   // --- OFFICE ADD-IN LOGIC ---
   
   if (Office.context.platform === Office.PlatformType.Mac) {
    // ==========================================
    // 1. MAC OS SPECIFIC FLOW (System Browser)
    // ==========================================
    console.log("Running in Mac Office. Using openBrowserWindow() with polling.");
    try {
     // Initiate Polling Session
     const initResponse = await fetch('/api/auth/initiate', { method: 'POST' });
     if (!initResponse.ok) throw new Error("Failed to initiate login session");
     const { loginId } = await initResponse.json();

     // Open System Browser
     const authUrl = `${location.origin}/auth-start.html?loginId=${loginId}`;
     
        
     Office.context.ui.openBrowserWindow(authUrl);

     // Start Polling for Token
     const pollInterval = setInterval(async () => {
      try {
     //  const pollResponse = await fetch(`/api/auth/poll?loginId=${loginId}`);
        const pollResponse = await fetch(`/api/auth/poll?loginId=${loginId}`, {
          credentials: 'include' 
         });   
       if (pollResponse.ok) {
        const data = await pollResponse.json();
        if (data.session_id) {
         clearInterval(pollInterval);
         console.log("Mac Authentication successful! Storing IAP token.");
          // SAVE THE TOKEN INTO THE SERVICE SO API CALLS CAN USE IT
         setIapTokenService(data.session_id);               
         setIapToken(data.session_id);
         setIsAuthenticating(false);
        }
       } else if (pollResponse.status === 400) {
        clearInterval(pollInterval);
        setAuthError("Login session expired or invalid. Please try again.");
        setIsAuthenticating(false);
       }
      } catch (pollErr) {
       console.warn("Poll check network error (retrying)...", pollErr);
      }
     }, 3000);
    } catch (err) {
     console.error(err);
     setAuthError(err.message || "Failed to start Mac login flow.");
     setIsAuthenticating(false);
    }

   } else {
    // ==========================================
    // 2. WINDOWS / WEB OFFICE FLOW (Dialog API)
    // ==========================================
    const authUrl = location.origin + '/auth-start.html';
    console.log("Running in Windows/Web Office. Using displayDialogAsync().");
    
    Office.context.ui.displayDialogAsync(authUrl, { height: 60, width: 40, promptBeforeOpen: true }, (asyncResult) => {
     if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      console.error("Dialog failed to open: " + asyncResult.error.message);
      setAuthError("Could not open authentication window.");
      setIsAuthenticating(false);
      return;
     }
     window.loginDialog = asyncResult.value;
     window.loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processAuthToken);
    });
   }

  } else {
   // ==========================================
   // 3. BROWSER FALLBACK LOGIC (window.open)
   // ==========================================
   const authUrl = location.origin + '/auth-start.html';
   console.log("Running in browser fallback. Using window.open().");
   const dialogWidth = 800;
   const dialogHeight = 600;
   window.loginDialog = window.open(authUrl, 'auth_dialog', `width=${dialogWidth},height=${dialogHeight}`);
   window.addEventListener("message", processAuthToken, { once: true });
  }
 };    
  return (
    <FluentProvider theme={webLightTheme}>
      <App
        title="AIScribe Simple Auth"
        onLoginRequest={startLoginFlow}
        initialIapToken={iapToken}
        isAuthenticating={isAuthenticating}
        authError={authError}
      />
    </FluentProvider>
  );
};

/**
 * =========================================================================
 * ROBUST INITIALIZER
 * =========================================================================
 */
function initialize() {
  const renderApp = () => {
    root.render(<AuthController />);
  };

  if (typeof Office !== "undefined") {
    console.log("Office.js detected. Using Office.onReady().");
    Office.onReady(renderApp);
  } else {
    console.log("Office.js not detected. Running in browser mode.");
    document.addEventListener("DOMContentLoaded", renderApp);
  }
}

// Start the initialization process.
initialize();

// --- Webpack Hot Module Replacement ---
if (module.hot) {
  module.hot.accept("./App", () => {
    const NextApp = require("./App").default;
    root?.render(NextApp);
  });
}